import { observable, action, runInAction } from "mobx";
import { RegistrationFormData } from "@data-types/membership/registration";
import { MitIdApiService } from "@api-services/integrations/MitIdApiService";
import { MitIdFormData } from "@data-types/integrations/MitIdFormData";
import { DateTime } from "luxon";
import {
	ETermRegErrorDescParam,
	ETermRegErrorParam,
	ETermRegParam,
} from "@data-types/integrations/MitIdRegistrationParams";
import { DeviceDetector, getCurrentCulture } from "@utils";
import { registrationValidationMultiple } from "@utils/membership/registrationValidation";
import {
	getRedirectOriginUrl,
	isCapacitorPlatform,
} from "@utils/specific/capacitor/helpers";
import { MainRegistrationStore } from "../MainRegistrationStore";
import { validateMitIdProcessResponse } from "@utils/lookups/validators";
import { PlatformRegistrationApiService } from "@api-services/platform";
import { redirect } from "@state/utils";
import { analyticsTracker } from "@utils/analytics";
import { RegistrationTypeEnum } from "@data-types/analytics-tracker/RegistrationTypeEnum";

export default class MitIdRegistrationStore extends MainRegistrationStore {
	@observable formData: MitIdFormData | null;

	constructor() {
		super();
	}

	@action.bound
	async initializeMitIdProcess(): Promise<void> {
		this.loader.suspend();

		const redirectUrl = getRedirectOriginUrl(
			window.location.pathname,
			true
		);

		try {
			const response =
				await MitIdApiService.getMitIdWidgetInfo(redirectUrl);
			const isValid = validateMitIdProcessResponse(
				response,
				"terminalRegistrationStore"
			);

			if (!isValid) {
				//invalid_token
				this.setMidIdErrorMessage(
					"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.TOKEN_EXCHANGE_RESPONSE_VALIDATION_FAILED"
				);
				//resume loader only on error
				this.loader.resume();
				return;
			}

			sessionStorage.setItem(
				"loginHash",
				JSON.stringify(response.loginHash)
			);
			//if we get here we need loader to still be on cause of redirect
			if (response.loginUrl) {
				window.location.replace(response.loginUrl);
			}
			if (isCapacitorPlatform()) {
				this.loader.resume();
			}
			//redirection - done
		} catch (err) {
			console.error(err);
			let errorMessage: string;
			//check what error messages should display in client
			switch (err?.data?.errorCode) {
				case 400955:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.AUTH_CONFIG_INIT";
					break;
				case 400956:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.AUTH_REQUEST_FAILED";
					break;
				case 400957:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.AUTH_UNHANDLED_ERROR";
					break;
				case 400958:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.AUTH_INVALID_LOGIN_URL";
					break;
				default:
					errorMessage = "registration:GENERIC_ERROR";
					break;
			}
			this.setMidIdErrorMessage(errorMessage);
			//resume loader only on error
			this.loader.resume();
		}
	}

	@action.bound
	async getUserAdditionalInfo(
		code: string,
		state: string,
		iss: string
	): Promise<void> {
		this.loader.suspend();
		const loginHashValues = sessionStorage.getItem("loginHash") as
			string | null;
		if (!loginHashValues) {
			//invalid_token
			this.setMidIdErrorMessage(
				"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.TOKEN_EXCHANGE_RESPONSE_VALIDATION_FAILED"
			);
			return;
		}
		const hashObject: { state: string; nonce: string } =
			JSON.parse(loginHashValues);

		const data = {
			code,
			state,
			iss,
			originalState: hashObject.state,
			originalNonce: hashObject.nonce,
			redirectUrl: getRedirectOriginUrl(window.location.pathname, true),
		};

		try {
			const response = await MitIdApiService.getUserAdditionalInfo(data);

			let formData: MitIdFormData = {};

			if (response.mitIdName) {
				const [firstName, ...lastNames] = response.mitIdName.split(" ");

				//if firstname does not exist, first name field will be empty and editable
				if (firstName) {
					formData.firstName = firstName;
				}
				//if lastnames do not exist, last name field will be empty and editable
				formData.lastName = lastNames ? lastNames.join(" ") : undefined;
			}
			if (response.cprNumber) {
				formData.personalIdentificator = response.cprNumber;
			}
			if (response.birthDate) {
				const datetime = DateTime.fromISO(response.birthDate);

				if (DeviceDetector.isMobileTheme) {
					formData.dob = {
						day: datetime.get("day").toString(),
						month: datetime.get("month").toString(),
						year: datetime.get("year").toString(),
					};
				} else {
					formData.dob = datetime.toFormat("yyyy-MM-dd");
				}
			}
			runInAction(() => {
				//used observable for setting field values and disabling fields
				this.formData = formData;
				sessionStorage.setItem(
					"additionalUserInfo",
					JSON.stringify(formData)
				);
			});
		} catch (err) {
			console.error(err);
			//check what error messages should display in client
			let errorMessage: string;
			switch (err?.data?.errorCode) {
				case 400959:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.TOKEN_EXCHANGE_CONFIG_INIT";
					break;
				case 400960:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.AUTH_MITID_FLOW_ERROR";
					break;
				case 400961:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.AUTH_RESPONSE_VALIDATION_FAILED";
					break;
				case 400962:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.TOKEN_EXCHANGE_REQUEST_FAILED";
					break;
				case 400963:
					errorMessage =
						"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.TOKEN_EXCHANGE_RESPONSE_VALIDATION_FAILED";
					break;
				default:
					errorMessage = "registration:GENERIC_ERROR";
					break;
			}
			this.setMidIdErrorMessage(errorMessage);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	setMidIdErrorMessage(message: string) {
		//every time error message is set, remove local storage keys
		this.removeMitIdProcessLocalStorageKeys();
		redirect(
			`/${getCurrentCulture()}/auth/registration/error?key=${message}`
		);
		//TODO: handle errors
		console.error(message);
	}

	@action.bound
	async onInitialize(): Promise<void> {
		analyticsTracker.trackRegistrationStarted(
			RegistrationTypeEnum.MitIdRegistration
		);

		const mitIdRegistrationInProgress = await this.mitIdUrlParamProcess();
		if (mitIdRegistrationInProgress) {
			//dont fetch lookups if full registration is not yet accessible
			return;
		}

		this.fetchLookups(() =>
			this.setMidIdErrorMessage("registration:GENERIC_ERROR")
		);
	}

	@action.bound
	async register(
		formData: RegistrationFormData,
		affiliateCode: string | null
	) {
		try {
			this.loader.suspend();

			const body = this.prepareRegistrationFormData(
				formData,
				affiliateCode
			);

			await PlatformRegistrationApiService.registerUser(body);

			this.removeMitIdProcessLocalStorageKeys();
			analyticsTracker.trackRegistrationFormCompleted();
			redirect(`/${getCurrentCulture()}/auth/registration/success`);
		} catch (error) {
			if (error?.data?.statusCode === 404) {
				//user card not found/not valid
				this.setMidIdErrorMessage(
					"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.CARD_NOT_FOUND"
				);
				return;
			}

			registrationValidationMultiple(
				error?.data,
				this.setMidIdErrorMessage
			);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	onDispose() {
		this.currentStepReactionDisposer?.();
		this.currentStep = 1;

		//lookups
		this.titleData = [];
		this.countryData = [];
		this.occupationData = [];
		this.languageData = [];
		this.countryRegionsData = [];
		this.occupationGroupData = [];
		this.gamingLimitsData = null;

		this.formData = null;
		//on user leave from Terminal Registration page
		this.removeMitIdProcessLocalStorageKeys();
	}

	/** @returns true if mitId login process in progress. */
	private mitIdUrlParamProcess = async (): Promise<boolean> => {
		const params = new URLSearchParams(window.location.search);

		if (params.get("code") && params.get("state") && params.get("iss")) {
			const paramsObject = {
				code: params.get("code"),
				state: params.get("state"),
				iss: params.get("iss"),
			};
			if (sessionStorage.getItem("additionalUserInfo")) {
				//do not fetch user additional info, use session storage values for prefill
				runInAction(() => {
					//used observable for setting field values and disabling fields
					this.formData = JSON.parse(
						sessionStorage.getItem("additionalUserInfo") as string
					);
				});
			} else if (
				paramsObject.code &&
				paramsObject.state &&
				paramsObject.iss
			) {
				await this.getUserAdditionalInfo(
					paramsObject.code,
					paramsObject.state,
					paramsObject.iss
				);
			}
		}

		// prettier-ignore
		if (
			params.get(ETermRegParam.ERROR) === ETermRegErrorParam.ACCESS_DENIED &&
			(
				params.get(ETermRegParam.ERROR_DESCRIPTION) === ETermRegErrorDescParam.USER_ABORT ||
				params.get(ETermRegParam.ERROR_DESCRIPTION) === ETermRegErrorDescParam.P_TO_B_ABORT
			)
		) {
			//user aborted on mitid ( cancel )
			this.removeMitIdProcessLocalStorageKeys();
			this.setMidIdErrorMessage(
					"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.MITID_USER_ABORTED"	
			);
			return true;
		}

		// prettier-ignore
		if (
			params.get(ETermRegParam.ERROR) === ETermRegErrorParam.ACCESS_DENIED &&
			(
				params.get(ETermRegParam.ERROR_DESCRIPTION) !== ETermRegErrorDescParam.USER_ABORT ||
				params.get(ETermRegParam.ERROR_DESCRIPTION) !== ETermRegErrorDescParam.P_TO_B_ABORT
			)
		) {
			//something went wrong with mit id
			/*
				possible errors:
				internal_error,
				no_ctx
			*/
			this.setMidIdErrorMessage(
					"specific:MEMBERSHIP.TERM_REGISTRATION.ERR_MSGS.MITID_GENERIC_ERROR"	
			);
			return true;
		}

		//user first landed on registration page
		if (
			!sessionStorage.getItem("loginHash") &&
			!sessionStorage.getItem("additionalUserInfo")
		) {
			this.initializeMitIdProcess();
			return true;
		}
		if (isCapacitorPlatform()) {
			this.loader.resume();
		}

		//fetch registration lookups if get here
		return false;
	};

	removeMitIdProcessLocalStorageKeys() {
		//clear session storage keys after successful registration/errors
		sessionStorage.removeItem("loginHash");
		sessionStorage.removeItem("additionalUserInfo");
	}
}
